import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, Loader2, UserCircle2, Shield } from "lucide-react";
import { toast } from "sonner";

export default function AdminUsers() {
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "engineer" });

  const load = () => api.get("/users").then((r) => setUsers(r.data));
  useEffect(() => {
    load();
  }, []);

  const create = async () => {
    if (!form.name || !form.email || !form.password) return toast.error("All fields required");
    try {
      await api.post("/users", form);
      toast.success("Engineer account created");
      setOpen(false);
      setForm({ name: "", email: "", password: "", role: "engineer" });
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Remove ${u.name}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed");
    }
  };

  if (!users)
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-7 w-7 animate-spin text-accent" />
      </div>
    );

  return (
    <div className="mx-auto max-w-3xl space-y-6">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-slate-500">Team</p>
          <h1 className="mt-1 font-heading text-3xl font-extrabold tracking-tight text-slate-900">Engineers</h1>
          <p className="mt-1 text-sm text-slate-500">Create and manage engineer & coordinator accounts.</p>
        </div>
        <Button onClick={() => setOpen(true)} className="field-lg bg-accent font-bold hover:bg-accent/90" data-testid="add-user-btn">
          <Plus className="mr-2 h-4 w-4" /> Add account
        </Button>
      </div>

      <div className="space-y-3">
        {users.map((u) => (
          <Card key={u.id} className="flex items-center justify-between border-slate-200 p-4 shadow-sm" data-testid={`user-row-${u.email}`}>
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-md bg-slate-100">
                {u.role === "admin" ? <Shield className="h-5 w-5 text-primary" /> : <UserCircle2 className="h-5 w-5 text-accent" />}
              </div>
              <div>
                <div className="font-heading font-bold text-slate-900">{u.name}</div>
                <div className="text-sm text-slate-500">{u.email}</div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Badge variant="outline" className={u.role === "admin" ? "border-primary text-primary" : "border-accent text-accent"}>
                {u.role}
              </Badge>
              {u.role !== "admin" && (
                <Button size="icon" variant="ghost" onClick={() => remove(u)} className="h-9 w-9 text-destructive" data-testid={`delete-user-${u.email}`}>
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add account</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Full name</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="field-lg" data-testid="user-name" />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="field-lg" data-testid="user-email" />
            </div>
            <div className="space-y-1.5">
              <Label>Temporary password</Label>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="field-lg" data-testid="user-password" />
            </div>
            <div className="space-y-1.5">
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger className="field-lg" data-testid="user-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="engineer">Engineer / Coordinator</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button onClick={create} className="bg-accent font-bold hover:bg-accent/90" data-testid="save-user-btn">Create account</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
